import _typeof from "@babel/runtime/helpers/esm/typeof";
import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["data", "dataTransform", "mapValueToProps", "onChange"];
import PropTypes from 'prop-types';
import React from 'react';
import { Select } from '../Select/Select';

/**
 * @extends Select
 * @since 5.0.0
 */
export var DataSelect = function DataSelect(_ref) {
  var data = _ref.data,
    dataTransform = _ref.dataTransform,
    mapValueToProps = _ref.mapValueToProps,
    onChange = _ref.onChange,
    props = _objectWithoutProperties(_ref, _excluded);
  var options = dataTransform(data).map(function (value) {
    return mapValueToProps(value);
  });
  var onSelectChange = function onSelectChange(e) {
    if (onChange) {
      var value;
      var index = 0;
      for (; index < options.length; index += 1) {
        if (e.target.value === "" + options[index].value) {
          value = options[index];
          break;
        }
      }
      onChange(e, value, index);
    }
  };
  return /*#__PURE__*/React.createElement(Select, _extends({}, props, {
    onChange: onSelectChange
  }), options.map(function (optionProps) {
    return /*#__PURE__*/React.createElement("option", optionProps);
  }));
};
DataSelect.displayName = 'DataSelect';
DataSelect.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The source data from which options will be constructed. If `data` is not already an array,
   * you will need to use `dataTransform` to turn it into one.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  data: PropTypes.any,
  /**
   * A transform function that will translate the source data into an array of values.
   * Arrays will be passed through directly by default.
   * Objects will be transformed into an array of objects with `key` and `value` properties.
   *
   *     dataTransform([1, 2, 3]);
   *     // [1, 2, 3]
   *     dataTransform({ a: 1, b: 2 });
   *     // [{ key: 'a', value: 1}, { key: 'b', value: 2 }]
   */
  dataTransform: PropTypes.func,
  /**
   * At the minimum, all `<option>` elements should receive `value`, `key`, and `children` props.
   * This function will translate the list value from `data` into an object including the three
   * required `<option>` props.
   *
   * By default, primitive values (i.e. strings, numbers) will be used as the `<option>` `value`
   * prop. For object values, `value`, `key`, and `children` will be pulled directly from
   * the object. When `key` is missing, it will fallback to `value`. When `children` is missing,
   * it will fallback first to `key`, and then to `value`.
   */
  mapValueToProps: PropTypes.func,
  /**
   * In addition to the standard change event, the onChange function will receive two other
   * parameters, `value` and `index`. `value` is the value represented by `data` after
   * `dataTransform` at the selected index. `index` is the index that was selected in the
   * `data` array.
   *
   * For example:
   *
   *     // When selecting 'b' from data: [{ key: 'a', value: 1}, { key: 'b', value: 2 }]
   *     const onDataSelectChange = (e, value, index) {
   *         console.log(e.target.value); // '2'
   *         console.log(value);          // { key: 'b', value: 2, children: 'b' }
   *         console.log(index);          // 1
   *     }
   */
  onChange: PropTypes.func.isRequired
} : {};
DataSelect.defaultProps = {
  dataTransform: function dataTransform(data) {
    if (Array.isArray(data)) {
      return data;
    }
    if (data && _typeof(data) === 'object') {
      return Object.keys(data).map(function (key) {
        return {
          key: key,
          value: data[key]
        };
      });
    }
    throw new Error("Cannot transform data of type: " + _typeof(data));
  },
  mapValueToProps: function mapValueToProps(val) {
    var value = val;
    var key;
    var children;
    if (val && _typeof(val) === 'object') {
      // eslint-disable-next-line prefer-destructuring
      value = val.value;
      // eslint-disable-next-line prefer-destructuring
      key = val.key;
      // eslint-disable-next-line prefer-destructuring
      children = val.children;
    }
    if (typeof key === 'undefined') {
      key = value;
    }
    if (typeof children === 'undefined') {
      children = key;
    }
    return {
      value: value,
      key: key,
      children: children
    };
  }
};